import { type CaseReducer, createSlice, type PayloadAction, type SliceSelectors } from '@reduxjs/toolkit';
import { PointLike } from 'mapbox-gl';
import type { ActivityType } from '@alltrails/types/ActivityType';
import type {
  AlgoliaHit,
  AreaSearchResult,
  ClusteredPOIResult,
  PhotospotSearchResult,
  POISearchResult,
  TrailSearchResult
} from '@alltrails/types/AlgoliaResults';
import { UserCustomRoute } from '@alltrails/types/CustomRoute';
import type { WaypointCategory, WaypointCategoryUid, WaypointId } from '@alltrails/types/Waypoint';
import type { WaypointMapSource } from '@alltrails/waypoints/types/WaypointMapSource';
import { Coordinates, LngLat } from '../types/Geo';
import { AllTrailsResult, SerializedCoordinates } from '../types/Results';
import { GeometryCoordinates } from '../types/Route';
import { AdminStyleSettings, BaseStyleId, BoundaryFilterType, DynamicStyleId, StaticStyleId } from '../types/Styles';
import type { TrailPriority } from '../types/TrailPriority';
import { defaultBaseStyleId } from '../utils/constants';
import { MapboxStyleIds } from '../utils/getMapboxStyleIds';
import parksToState from '../utils/parksToState';
import { coordinatesToSerializedCoordinates } from '../utils/serializedCoordinates';
import { dynamicStyleCardConfigs } from '../utils/styleCardConfigs';
import { isDynamicStyle, isStaticStyle, styleSupports3d } from '../utils/styleIdHelpers';

export type TrailResultOverrides = {
  priority?: TrailPriority; // When present overrides popularity sorting and takes precedence in pin styling
  isActive?: boolean; // Manual override of active state to show a trail pin as active when it is not the active trail
};
export type TrailResult = TrailSearchResult & TrailResultOverrides;

export type NonTrailListResult = {
  ID: number;
  _geoloc: { lat: number; lng: number };
  name: string;
  slug?: string;
  type: 'map' | 'track';
  meters?: number;
  durationMinutes?: number;
  elevationGain?: number;
  activityType?: ActivityType;
  createdAt?: string;
  locationName?: string;
};

export type ZoomBucket = 4 | 6 | 8 | 12 | 14 | 23;

// Overlays that have a popup associated with them:
export const overlaysWithPopups: DynamicStyleId[] = ['publicLands', 'privateLands', 'wildfire'];

const persistedMapKeys = ['mapboxStyleIds'] as const;

const persistedMapStyleKeys = [
  'adminStyleSettings',
  'baseStyleId',
  'currentCoordinates',
  'dynamicStyleIds',
  'heatmapStyleIds',
  'is3dActive',
  'staticStyleIds',
  'supportedDynamicStyleIds'
] as const;

export type PersistedMapKeys = (typeof persistedMapKeys | typeof persistedMapStyleKeys)[number];

/**
 * Payload for a community waypoint, extracted from a vector tile feature on click.
 */
export type CommunityWaypointPayload = {
  id: WaypointId;
  name: string;
  description?: string;
  latitude: number;
  longitude: number;
  categoryUid?: WaypointCategoryUid;
  createdAt?: string;
};

/**
 * The waypoint currently shown in the sidebar. Discriminated by `source`: `'user'` for waypoints
 * the viewer owns (editable, looked up via RTK Query) and `'community'` for everyone else's
 * (view-only, payload baked in since there's no fetch endpoint).
 */
export type SelectedWaypoint =
  | { source: Extract<WaypointMapSource, 'user'>; id: WaypointId }
  | { source: Extract<WaypointMapSource, 'community'>; data: CommunityWaypointPayload };

export type MapState = {
  currentCoordinates?: Coordinates; // Coordinates for the blue-dot marker, representing a "current" location on a map
  elevationCoordinates?: Coordinates; // Coordinates representing the hovered location on an elevation chart
  routeCoordinates?: LngLat[]; // Coordinates representing a route polyline

  allowClickEvents?: boolean; // Whether the map should allow click events - defaults to true
  cursor?: string; // CSS cursor to display on the map
  is3dActive?: boolean; // Whether 3d is currently active
  mapboxStyleIds: MapboxStyleIds; // Environment specific ids for our map styles
  mapZoomBucket?: number; // The current zoom level of the map, bucketed to avoid excessive rendering of pins, to determine collapsed / filter logic for unclustering experiment

  clickedResult?: AllTrailsResult; // Algolia result that has been clicked
  hoveredAdminResult?: AlgoliaHit & { popupOffset?: PointLike; suppressPopup?: boolean; renderSeparately?: boolean }; // Algolia result that is currently hovered - this is NOT admin specific anymore as it is also used in the map overlays
  hoveredResult?: AllTrailsResult; // Algolia result that is currently hovered
  clickedSerializedCoordinates?: SerializedCoordinates; // Serialized coordinates of the clicked result (trail, map, or activity)
  hoveredSerializedCoordinates?: SerializedCoordinates; // Serialized coordinates of the hovered result (trail, map, or activity)
  trailheadResults?: string[]; // Array of result (trail, map, or activity) ids that are in the currently hovered or clicked trailhead
  routesById: Record<string, GeometryCoordinates | undefined>; // A record where the key is the result (trail, map, or activity) id and the value is the "route" data (polyline) -- used to render <Polyline />

  activeTrails?: TrailResult[]; // Trail result(s) that are hovered/selected
  trailResults?: Record<string, TrailResult[]>; // Record of the coordinates (lat:lng) of a trailhead to the trail result(s) from that trailhead
  hideTrailPins?: boolean; // POC: when true, suppress rendering of trail pin layers (used by the polyline heatmap POC controls)

  nonTrailListResults?: NonTrailListResult[]; // Map and track items from a saved list
  hoveredListItemId?: number; // ID of a non-trail list item hovered in the sidebar
  viewedPinSerializedCoordinatesThisSession: SerializedCoordinates[]; // Serialized coordinates of trail pins clicked during the current session (not persisted).

  // Park-specific data that follows the new patterns established with the TrailPins components
  activePark?: AreaSearchResult;
  parkResults?: Record<string, AreaSearchResult>; // Record of the coordinates (lat:lng) of a park to the park object

  // POI-specific data
  activePOI?: ClusteredPOIResult; // POI result that is active (clicked)
  hoveredPOI?: POISearchResult; // POI result that is hovered
  selectedPOI?: POISearchResult; // Selected POI for detail view
  isMapFeatureSelected?: boolean; // Whether a map feature (e.g. featureset POI) is selected, used to block map click actions
  pendingFlyTo?: { center: [number, number]; zoom: number }; // Queued flyTo for cross-route navigation where direct flyTo would be clobbered by replaceState
  poiInteractionBehavior?: 'enlarge' | 'popup';
  showPOIPopupMenu?: boolean; // Whether the POI popup menu is shown, used only if `poiInteractionBehavior` is `popup`
  poiResults?: Record<string, POISearchResult>;
  hiddenPOIs?: number[]; // POI/photospot ids that are hidden from the map
  renderedPOIs?: Record<'count', number>; // Number of POIs rendered on the map. Uses a Record to trigger a rerender anytime an update is made, even if POI count is the same.
  respectPOIRenderLimit?: boolean; // Whether the map should respect the POI render count limit. Defaults to true.

  // photospots
  photospotResults?: Record<string, PhotospotSearchResult>;

  //User Custom Routes
  userCustomRoutes: Record<string, UserCustomRoute>;
  activeUserCustomRoute?: UserCustomRoute;
  displayedUserCustomRouteIds: number[];
  activeRouteIds?: number[];

  // User Recordings
  userRecordings: Record<string, UserCustomRoute>;
  displayedUserRecordingIds: number[];

  // The waypoint currently being viewed in the sidebar.
  // Tagged union — `'user'` waypoints are looked up via RTK Query (editable);
  // `'community'` waypoints carry their full payload from the vector tile (view-only).
  selectedWaypoint?: SelectedWaypoint;
  userWaypointDraft?: {
    id: WaypointId;
    name?: string;
    showTitle?: boolean;
    waypointCategory?: WaypointCategory;
    // Set when the user drags the selected pin in edit mode. The form re-syncs its coordinates input
    // off `EditMapWaypointFormValues.coordinates`, which the sidebar derives from these fields.
    latitude?: number;
    longitude?: number;
  };

  // Active category filter for the community-waypoints layer.
  communityWaypointCategories: WaypointCategoryUid[];

  // Deprecated park data that is based around the old area_presenter in the monolith which doesn't use the algolia area types :/
  allParkCoordinates?: Coordinates[];
  clickedPark?: AllTrailsResult;
  hoveredPark?: AllTrailsResult;
  clickedSerializedParkCoordinates?: SerializedCoordinates;
  hoveredSerializedParkCoordinates?: SerializedCoordinates;
  parkIdsBySerializedCoordinates: Record<SerializedCoordinates, string[]>;
  parksById: Record<string, AllTrailsResult>;

  // Map style/overlay data
  adminStyleSettings?: AdminStyleSettings; // Admin-set settings for specific styles
  baseStyleId: BaseStyleId; // Applied base map style
  dynamicStyleIds: DynamicStyleId[]; // Applied dynamic styles
  heatmapStyleIds: DynamicStyleId[]; // Some DynamicStyleIds have an optional heatmap layer that can also be applied to the map - this will _not_ include the basic heatmap layer
  isOverlayTruncated: boolean; // Whether the last geo-boundary overlay response was truncated (hit the result limit)
  loadingOverlays: string[]; // IDs of overlays currently loading; show loading indicator when non-empty
  staticStyleIds: StaticStyleId[]; // Applied static styles
  supportedDynamicStyleIds: DynamicStyleId[]; // Which dynamic styles are enabled for the given map
  trailId?: number; // When a map is displaying a single trail, the heatmap overlay behaves differently
};

export const initialState: MapState = {
  currentCoordinates: undefined,
  elevationCoordinates: undefined,
  routeCoordinates: undefined,

  allowClickEvents: true,
  cursor: undefined,
  is3dActive: false,
  mapboxStyleIds: {
    mapbox3DBaseMapStyleId: '',
    mapboxAllTrailsStyleId: '',
    mapboxAllTrailsStyleIdV2: '',
    mapboxSatelliteStyleId: '',
    mapboxSatelliteStyleIdV2: '',
    mapboxTerrainStyleId: '',
    mapboxWorldTopoTerrainStyleId: ''
  },
  mapZoomBucket: undefined,

  clickedResult: undefined,
  hoveredAdminResult: undefined,
  clickedSerializedCoordinates: undefined,
  hoveredSerializedCoordinates: undefined,
  hoveredResult: undefined,
  trailheadResults: undefined,
  routesById: {},
  viewedPinSerializedCoordinatesThisSession: [],
  nonTrailListResults: undefined,
  hoveredListItemId: undefined,

  allParkCoordinates: [],
  clickedPark: undefined,
  hoveredPark: undefined,
  clickedSerializedParkCoordinates: undefined,
  hoveredSerializedParkCoordinates: undefined,
  parkIdsBySerializedCoordinates: {},
  parksById: {},

  activePOI: undefined,
  hoveredPOI: undefined,
  poiInteractionBehavior: 'popup',
  selectedPOI: undefined,
  isMapFeatureSelected: false,
  showPOIPopupMenu: false,
  respectPOIRenderLimit: true,

  userCustomRoutes: {},
  activeUserCustomRoute: undefined,
  displayedUserCustomRouteIds: [],
  activeRouteIds: undefined,

  userRecordings: {},
  displayedUserRecordingIds: [],

  selectedWaypoint: undefined,
  userWaypointDraft: undefined,

  // All non-`other` categories visible by default.
  communityWaypointCategories: ['camping', 'water', 'navigation', 'landmark', 'event-marker'],

  adminStyleSettings: undefined,
  baseStyleId: defaultBaseStyleId,
  heatmapStyleIds: [],
  dynamicStyleIds: [],
  isOverlayTruncated: false,
  loadingOverlays: [],
  staticStyleIds: [],
  supportedDynamicStyleIds: []
};

type ResetStatePayload = {
  persistKeys?: (keyof Omit<MapState, PersistedMapKeys>)[];
  resetStyleKeys?: boolean;
  initialState?: Partial<MapState>;
};

type MapReducers = {
  resetState: CaseReducer<MapState, PayloadAction<ResetStatePayload>>;
  updateMapZoomBucket: CaseReducer<MapState, PayloadAction<number>>;
  updateCurrentCoordinates: CaseReducer<MapState, PayloadAction<Coordinates | undefined>>;
  updateElevationCoordinates: CaseReducer<MapState, PayloadAction<Coordinates | undefined>>;
  updateRouteCoordinates: CaseReducer<MapState, PayloadAction<LngLat[] | undefined>>;
  updateCursor: CaseReducer<MapState, PayloadAction<string | undefined>>;
  updateIs3dActive: CaseReducer<MapState, PayloadAction<boolean>>;
  updateClickedResult: CaseReducer<MapState, PayloadAction<AllTrailsResult | undefined>>;
  updateHoveredAdminResult: CaseReducer<MapState, PayloadAction<AlgoliaHit | undefined>>;
  updateHoveredResult: CaseReducer<MapState, PayloadAction<AllTrailsResult | undefined>>;
  clearClickedCoordinates: CaseReducer<MapState, PayloadAction<void>>;
  updateAdminStyleSettings: CaseReducer<MapState, PayloadAction<AdminStyleSettings | undefined>>;
  addOrRemoveBoundaryFilter: CaseReducer<MapState, PayloadAction<BoundaryFilterType>>;
  updateBaseStyleId: CaseReducer<MapState, PayloadAction<BaseStyleId>>;
  toggleDynamicStyleId: CaseReducer<MapState, PayloadAction<DynamicStyleId>>;
  toggleHeatmapStyleId: CaseReducer<MapState, PayloadAction<DynamicStyleId>>;
  toggleStaticStyleId: CaseReducer<MapState, PayloadAction<StaticStyleId>>;
  updateDynamicStyleIds: CaseReducer<MapState, PayloadAction<DynamicStyleId[]>>;
  updateHeatmapStyleIds: CaseReducer<MapState, PayloadAction<DynamicStyleId[]>>;
  updateStaticStyleIds: CaseReducer<MapState, PayloadAction<StaticStyleId[]>>;
  updateSupportedDynamicStyleIds: CaseReducer<MapState, PayloadAction<DynamicStyleId[]>>;
  updateRoutesById: CaseReducer<MapState, PayloadAction<{ resultId?: string; routeCoordinates: GeometryCoordinates }>>;
  updateAllowClickEvents: CaseReducer<MapState, PayloadAction<boolean>>;
  updateActiveTrails: CaseReducer<MapState, PayloadAction<TrailResult[] | undefined>>;
  updateNonTrailListResults: CaseReducer<MapState, PayloadAction<NonTrailListResult[] | undefined>>;
  updateHoveredListItemId: CaseReducer<MapState, PayloadAction<number | undefined>>;
  addViewedPinSerializedCoordinatesThisSession: CaseReducer<MapState, PayloadAction<SerializedCoordinates>>;
  updateTrailResults: CaseReducer<MapState, PayloadAction<TrailResult[]>>;
  setHideTrailPins: CaseReducer<MapState, PayloadAction<boolean>>;
  updateActivePark: CaseReducer<MapState, PayloadAction<AreaSearchResult | undefined>>;
  updateParkResults: CaseReducer<MapState, PayloadAction<AreaSearchResult[]>>;
  updateActivePOI: CaseReducer<MapState, PayloadAction<ClusteredPOIResult | undefined>>;
  updateHoveredPOI: CaseReducer<MapState, PayloadAction<POISearchResult | undefined>>;
  updateSelectedPOI: CaseReducer<MapState, PayloadAction<POISearchResult | undefined>>;
  updateIsMapFeatureSelected: CaseReducer<MapState, PayloadAction<boolean>>;
  setPendingFlyTo: CaseReducer<MapState, PayloadAction<{ center: [number, number]; zoom: number }>>;
  clearPendingFlyTo: CaseReducer<MapState, PayloadAction<void>>;
  updateShowPOIPopupMenu: CaseReducer<MapState, PayloadAction<boolean>>;
  updateHiddenPOIs: CaseReducer<MapState, PayloadAction<number[]>>;
  updatePoiResults: CaseReducer<MapState, PayloadAction<POISearchResult[]>>;
  updateRenderedPOIs: CaseReducer<MapState, PayloadAction<number>>;
  updatePhotospotResults: CaseReducer<MapState, PayloadAction<PhotospotSearchResult[]>>;
  updateHoveredParkCoordinates: CaseReducer<MapState, PayloadAction<SerializedCoordinates>>;
  clearHoveredParkCoordinates: CaseReducer<MapState, PayloadAction<void>>;
  updateClickedParkCoordinates: CaseReducer<MapState, PayloadAction<{ coordinates?: SerializedCoordinates }>>;
  clearClickedParkCoordinates: CaseReducer<MapState, PayloadAction<void>>;
  updateParks: CaseReducer<MapState, PayloadAction<AllTrailsResult[]>>;
  setUserCustomRoutes: CaseReducer<MapState, PayloadAction<Record<string, UserCustomRoute>>>;
  setDisplayedUserCustomRouteIds: CaseReducer<MapState, PayloadAction<number[]>>;
  removeUserCustomRoute: CaseReducer<MapState, PayloadAction<number>>;
  setActiveRouteIds: CaseReducer<MapState, PayloadAction<number[] | undefined>>;
  setUserRecordings: CaseReducer<MapState, PayloadAction<Record<string, UserCustomRoute>>>;
  setDisplayedUserRecordingIds: CaseReducer<MapState, PayloadAction<number[]>>;
  removeUserRecording: CaseReducer<MapState, PayloadAction<number>>;
  setSelectedWaypoint: CaseReducer<MapState, PayloadAction<SelectedWaypoint>>;
  clearSelectedWaypoint: CaseReducer<MapState, PayloadAction<void>>;
  setUserWaypointDraft: CaseReducer<MapState, PayloadAction<NonNullable<MapState['userWaypointDraft']>>>;
  clearUserWaypointDraft: CaseReducer<MapState, PayloadAction<void>>;
  setCommunityWaypointCategories: CaseReducer<MapState, PayloadAction<WaypointCategoryUid[]>>;
  toggleCommunityWaypointCategory: CaseReducer<MapState, PayloadAction<WaypointCategoryUid>>;
  setOverlayLoading: CaseReducer<MapState, PayloadAction<{ id: string; isLoading: boolean }>>;
  setOverlayTruncated: CaseReducer<MapState, PayloadAction<boolean>>;
};

export const mapSlice = createSlice<MapState, MapReducers, 'map', SliceSelectors<MapState>, 'map'>({
  name: 'map',
  initialState,
  reducers: {
    resetState: (state: MapState, action: PayloadAction<ResetStatePayload>) => {
      const newState: MapState = { ...initialState, ...(action.payload.initialState || {}) };
      action.payload.persistKeys?.forEach(key => {
        const persistValue = state[key];
        (newState[key] as typeof persistValue) = persistValue;
      });
      persistedMapKeys.forEach(key => {
        const persistValue = state[key];
        (newState[key] as typeof persistValue) = persistValue;
      });
      if (!action.payload.resetStyleKeys) {
        persistedMapStyleKeys.forEach(key => {
          const persistValue = state[key];
          (newState[key] as typeof persistValue) = persistValue;
        });
      }

      // Remove any unsupported dynamic styles
      newState.dynamicStyleIds = newState.dynamicStyleIds.filter(id => newState.supportedDynamicStyleIds.includes(id));
      return newState;
    },
    updateMapZoomBucket: (state, action: PayloadAction<number>) => {
      state.mapZoomBucket = action.payload;
    },
    updateCurrentCoordinates: (state, action: PayloadAction<Coordinates | undefined>) => {
      state.currentCoordinates = action.payload;
    },
    updateElevationCoordinates: (state, action: PayloadAction<Coordinates | undefined>) => {
      state.elevationCoordinates = action.payload;
    },
    updateRouteCoordinates: (state, action: PayloadAction<LngLat[] | undefined>) => {
      state.routeCoordinates = action.payload;
    },

    updateCursor: (state, action: PayloadAction<string | undefined>) => {
      state.cursor = action.payload;
    },
    updateIs3dActive: (state, action: PayloadAction<boolean>) => {
      state.is3dActive = action.payload;
    },
    updateClickedResult: (state, action: PayloadAction<AllTrailsResult | undefined>) => {
      state.clickedResult = action.payload;
    },
    updateHoveredAdminResult: (state, action: PayloadAction<AlgoliaHit | undefined>) => {
      state.hoveredAdminResult = action.payload;
    },
    updateHoveredResult: (state, action: PayloadAction<AllTrailsResult | undefined>) => {
      state.hoveredResult = action.payload;
    },
    clearClickedCoordinates: state => {
      state.clickedResult = undefined;
      state.hoveredResult = undefined;
      state.hoveredSerializedCoordinates = undefined;
      state.clickedSerializedCoordinates = undefined;
      state.trailheadResults = undefined;
    },
    updateAdminStyleSettings: (state, action: PayloadAction<AdminStyleSettings | undefined>) => {
      state.adminStyleSettings = action.payload;
    },
    addOrRemoveBoundaryFilter: (state, action: PayloadAction<BoundaryFilterType>) => {
      const indexToRemove = state.adminStyleSettings?.boundaryFilters?.indexOf(action.payload);
      if (indexToRemove !== undefined && indexToRemove > -1) {
        state.adminStyleSettings?.boundaryFilters?.splice(indexToRemove, 1);
      } else {
        if (!state.adminStyleSettings?.boundaryFilters) {
          state.adminStyleSettings = { ...state.adminStyleSettings, boundaryFilters: [action.payload] };
        } else {
          state.adminStyleSettings?.boundaryFilters?.push(action.payload);
        }
      }
    },
    updateBaseStyleId: (state, action: PayloadAction<BaseStyleId>) => {
      const styleId = action.payload;
      if (!styleSupports3d(styleId)) {
        state.is3dActive = false;
      }
      state.baseStyleId = styleId;
    },
    toggleDynamicStyleId: (state, action: PayloadAction<DynamicStyleId>) => {
      const styleId = action.payload;
      const ids = new Set(state.dynamicStyleIds);
      if (ids.has(styleId)) {
        ids.delete(styleId);

        // Turn off the corresponding heatmap if the dynamic style is deselected
        if (dynamicStyleCardConfigs[styleId].hasHeatmap) {
          const heatmapIds = new Set(state.heatmapStyleIds);
          if (heatmapIds.has(styleId)) {
            heatmapIds.delete(styleId);
            state.heatmapStyleIds = Array.from(heatmapIds);
          }
        }
      } else {
        ids.add(styleId);
      }
      state.dynamicStyleIds = Array.from(ids);
    },
    toggleHeatmapStyleId: (state, action: PayloadAction<DynamicStyleId>) => {
      const ids = new Set(state.heatmapStyleIds);
      if (ids.has(action.payload)) {
        ids.delete(action.payload);
      } else {
        ids.add(action.payload);
      }
      state.heatmapStyleIds = Array.from(ids);
    },
    toggleStaticStyleId: (state, action: PayloadAction<StaticStyleId>) => {
      const ids = new Set(state.staticStyleIds);
      if (ids.has(action.payload)) {
        ids.delete(action.payload);
      } else {
        ids.add(action.payload);
      }
      state.staticStyleIds = Array.from(ids);
    },
    updateDynamicStyleIds: (state, action: PayloadAction<DynamicStyleId[]>) => {
      let styleIds = action.payload.filter((id): id is DynamicStyleId => isDynamicStyle(id));
      if (state.supportedDynamicStyleIds.length > 0) {
        styleIds = styleIds.filter(id => state.supportedDynamicStyleIds.includes(id));
      }
      state.dynamicStyleIds = styleIds;
    },
    updateHeatmapStyleIds: (state, action: PayloadAction<DynamicStyleId[]>) => {
      let styleIds = action.payload.filter((id): id is DynamicStyleId => isDynamicStyle(id) && Boolean(dynamicStyleCardConfigs[id].hasHeatmap));
      if (state.supportedDynamicStyleIds.length > 0) {
        styleIds = styleIds.filter(id => state.supportedDynamicStyleIds.includes(id));
      }
      state.heatmapStyleIds = styleIds;
    },
    updateStaticStyleIds: (state, action: PayloadAction<StaticStyleId[]>) => {
      const styleIds = action.payload.filter((id): id is StaticStyleId => isStaticStyle(id));
      state.staticStyleIds = styleIds;
    },
    updateSupportedDynamicStyleIds: (state, action: PayloadAction<DynamicStyleId[]>) => {
      const previousSupported = state.supportedDynamicStyleIds;
      const supportedDynamicStyleIds = action.payload;
      state.supportedDynamicStyleIds = supportedDynamicStyleIds;
      // Remove any unsupported dynamic styles
      state.dynamicStyleIds = state.dynamicStyleIds.filter(id => supportedDynamicStyleIds.includes(id));
      // Auto-enable styles flagged defaultEnabled on the unsupported → supported transition
      supportedDynamicStyleIds.forEach(id => {
        if (!previousSupported.includes(id) && dynamicStyleCardConfigs[id].defaultEnabled && !state.dynamicStyleIds.includes(id)) {
          state.dynamicStyleIds.push(id);
        }
      });
    },
    updateRoutesById: (state, action: PayloadAction<{ resultId?: string; routeCoordinates: GeometryCoordinates }>) => {
      const { resultId, routeCoordinates } = action.payload;
      const isValidResponse = !!resultId && !!routeCoordinates;
      if (isValidResponse) {
        // Update the store even if this result is no longer selected to avoid future API calls
        state.routesById[resultId] = routeCoordinates;
      }
    },
    updateAllowClickEvents: (state, action: PayloadAction<boolean>) => {
      state.allowClickEvents = action.payload;
    },
    updateActiveTrails: (state, action: PayloadAction<TrailResult[] | undefined>) => {
      state.activeTrails = action.payload;
      state.hoveredPOI = undefined;
    },
    updateNonTrailListResults: (state, action: PayloadAction<NonTrailListResult[] | undefined>) => {
      state.nonTrailListResults = action.payload;
    },
    updateHoveredListItemId: (state, action: PayloadAction<number | undefined>) => {
      state.hoveredListItemId = action.payload;
    },
    addViewedPinSerializedCoordinatesThisSession: (state, action: PayloadAction<SerializedCoordinates>) => {
      const coords = action.payload;
      const viewed = new Set(state.viewedPinSerializedCoordinatesThisSession);
      if (!viewed.has(coords)) {
        viewed.add(coords);
        state.viewedPinSerializedCoordinatesThisSession = Array.from(viewed);
      }
    },
    updateTrailResults: (state, action: PayloadAction<TrailResult[]>) => {
      let activeTrailCount = 0;
      const activeTrailIds = state.activeTrails?.map(trail => trail.ID);
      const trailResults: Record<string, TrailResult[]> = action.payload.reduce<Record<string, TrailResult[]>>((newResults, result) => {
        if (!result._geoloc) {
          return newResults;
        }
        if (activeTrailIds?.includes(result.ID)) {
          activeTrailCount += 1;
        }
        const trailheadCoordinates = coordinatesToSerializedCoordinates(result._cluster_geoloc ?? result._geoloc);
        const resultsAtCoordinates = newResults[trailheadCoordinates] ?? [];
        return { ...newResults, [trailheadCoordinates]: [...resultsAtCoordinates, result] };
      }, {});
      state.trailResults = trailResults;

      // Reset activeTrails if the current active trails are not all contained in the new trail results
      if (activeTrailCount !== state.activeTrails?.length) {
        state.activeTrails = undefined;
      }
    },
    setHideTrailPins: (state, action: PayloadAction<boolean>) => {
      state.hideTrailPins = action.payload;
    },
    updateActivePark: (state, action: PayloadAction<AreaSearchResult | undefined>) => {
      state.activePark = action.payload;
    },
    updateParkResults: (state, action: PayloadAction<AreaSearchResult[]>) => {
      let hasActivePark = false;
      const parkResults: Record<string, AreaSearchResult> = action.payload.reduce<Record<string, AreaSearchResult>>((newResults, result) => {
        if (!result._geoloc) {
          return newResults;
        }
        if (state.activePark && state.activePark.ID === result.ID) {
          hasActivePark = true;
        }
        const serializedCoordinates = coordinatesToSerializedCoordinates(result._geoloc);
        return { ...newResults, [serializedCoordinates]: result };
      }, {});

      // Compare old and new results to prevent unnecessary rerenders
      const previousParkCoordinates = Object.keys(state.parkResults || {});
      const newParkCoordinates = Object.keys(parkResults);
      if (
        previousParkCoordinates.length !== newParkCoordinates.length ||
        !previousParkCoordinates.every(coords => newParkCoordinates.includes(coords))
      ) {
        state.parkResults = parkResults;

        if (!hasActivePark) {
          state.activePark = undefined;
        }
      }
    },
    updateActivePOI: (state, action: PayloadAction<ClusteredPOIResult | undefined>) => {
      state.activePOI = action.payload;
    },
    updateHoveredPOI: (state, action: PayloadAction<POISearchResult | undefined>) => {
      state.activeTrails = undefined;
      state.hoveredPOI = action.payload;
    },
    updateSelectedPOI: (state, action: PayloadAction<POISearchResult | undefined>) => {
      state.selectedPOI = action.payload;
    },
    updateIsMapFeatureSelected: (state, action: PayloadAction<boolean>) => {
      state.isMapFeatureSelected = action.payload;
    },
    setPendingFlyTo: (state, action: PayloadAction<{ center: [number, number]; zoom: number }>) => {
      state.pendingFlyTo = action.payload;
    },
    clearPendingFlyTo: state => {
      state.pendingFlyTo = undefined;
    },
    updateShowPOIPopupMenu: (state, action: PayloadAction<boolean>) => {
      state.showPOIPopupMenu = action.payload;
    },
    updateHiddenPOIs: (state, action: PayloadAction<number[]>) => {
      state.hiddenPOIs = action.payload;
    },
    updatePoiResults: (state, action: PayloadAction<POISearchResult[]>) => {
      const poiResults: Record<string, POISearchResult> = action.payload.reduce<Record<string, POISearchResult>>((newResults, result) => {
        if (!result._geoloc) {
          return newResults;
        }
        const serializedCoordinates = coordinatesToSerializedCoordinates(result._geoloc);
        return { ...newResults, [serializedCoordinates]: result };
      }, {});
      state.poiResults = poiResults;
    },
    updateRenderedPOIs: (state, action: PayloadAction<number>) => {
      state.renderedPOIs = { count: action.payload };
    },
    updatePhotospotResults: (state, action: PayloadAction<PhotospotSearchResult[]>) => {
      const photospotResults: Record<string, PhotospotSearchResult> = action.payload.reduce<Record<string, PhotospotSearchResult>>(
        (newResults, result) => {
          if (!result._geoloc) {
            return newResults;
          }
          const serializedCoordinates = coordinatesToSerializedCoordinates(result._geoloc);
          return { ...newResults, [serializedCoordinates]: result };
        },
        {}
      );
      state.photospotResults = photospotResults;
    },
    updateHoveredParkCoordinates: (state, action: PayloadAction<SerializedCoordinates>) => {
      const hoveredCoordinates = action.payload || state.hoveredSerializedParkCoordinates;
      const isNewHoveredPark = action.payload !== state.hoveredSerializedParkCoordinates;

      if (!state.clickedPark && isNewHoveredPark) {
        state.hoveredSerializedParkCoordinates = hoveredCoordinates;
        const parkIds = hoveredCoordinates && state.parkIdsBySerializedCoordinates[hoveredCoordinates];
        const newParkId = parkIds?.[0];
        state.hoveredPark = newParkId ? state.parksById[newParkId] : undefined;
      }
    },
    clearHoveredParkCoordinates: state => {
      if (!state.clickedSerializedParkCoordinates && state.hoveredPark) {
        state.hoveredSerializedParkCoordinates = undefined;
        state.hoveredPark = undefined;
      }
    },
    updateClickedParkCoordinates: (state, action: PayloadAction<{ coordinates?: SerializedCoordinates }>) => {
      const { coordinates } = action.payload;
      const clickedSerializedCoordinates = coordinates || state.hoveredSerializedParkCoordinates;
      state.clickedSerializedParkCoordinates = clickedSerializedCoordinates;
      const parkIds = clickedSerializedCoordinates ? state.parkIdsBySerializedCoordinates[clickedSerializedCoordinates] : undefined;
      state.clickedPark = parkIds && state.parksById[parkIds[0]];
      state.hoveredSerializedParkCoordinates = undefined;
      state.hoveredPark = undefined;
    },
    clearClickedParkCoordinates: state => {
      state.clickedPark = undefined;
      state.hoveredPark = undefined;
      state.hoveredSerializedParkCoordinates = undefined;
      state.clickedSerializedParkCoordinates = undefined;
    },
    updateParks: (state, action: PayloadAction<AllTrailsResult[]>) => {
      const newParks = action.payload;
      const { parkIdsBySerializedCoordinates, parksById, allParkCoordinates } = parksToState(newParks, state);

      state.allParkCoordinates = allParkCoordinates;
      state.parkIdsBySerializedCoordinates = parkIdsBySerializedCoordinates;
      state.parksById = parksById;
    },
    setUserCustomRoutes: (state, action: PayloadAction<Record<string, UserCustomRoute>>) => {
      state.userCustomRoutes = action.payload;
    },
    setDisplayedUserCustomRouteIds: (state, action: PayloadAction<number[]>) => {
      state.displayedUserCustomRouteIds = action.payload;
    },
    removeUserCustomRoute: (state, action) => {
      const mapId = action.payload;
      delete state.userCustomRoutes[mapId];
    },
    setActiveRouteIds: (state, action: PayloadAction<number[] | undefined>) => {
      state.activeRouteIds = action.payload;
    },
    setUserRecordings: (state, action: PayloadAction<Record<string, UserCustomRoute>>) => {
      state.userRecordings = action.payload;
    },
    setDisplayedUserRecordingIds: (state, action: PayloadAction<number[]>) => {
      state.displayedUserRecordingIds = action.payload;
    },
    removeUserRecording: (state, action: PayloadAction<number>) => {
      const mapId = action.payload;
      delete state.userRecordings[mapId];
    },
    setSelectedWaypoint: (state, action: PayloadAction<SelectedWaypoint>) => {
      state.selectedWaypoint = action.payload;
    },
    clearSelectedWaypoint: state => {
      state.selectedWaypoint = undefined;
    },
    setUserWaypointDraft: (state, action: PayloadAction<NonNullable<MapState['userWaypointDraft']>>) => {
      // Shallow-merge when the draft is for the same waypoint so partial updates from different sources
      // (form fields vs. drag-set coordinates) don't clobber each other.
      if (state.userWaypointDraft?.id === action.payload.id) {
        state.userWaypointDraft = { ...state.userWaypointDraft, ...action.payload };
      } else {
        state.userWaypointDraft = action.payload;
      }
    },
    clearUserWaypointDraft: state => {
      state.userWaypointDraft = undefined;
    },
    setCommunityWaypointCategories: (state, action: PayloadAction<WaypointCategoryUid[]>) => {
      state.communityWaypointCategories = action.payload;
    },
    toggleCommunityWaypointCategory: (state, action: PayloadAction<WaypointCategoryUid>) => {
      const uid = action.payload;
      const current = new Set(state.communityWaypointCategories);
      if (current.has(uid)) {
        current.delete(uid);
      } else {
        current.add(uid);
      }
      state.communityWaypointCategories = Array.from(current);
    },
    setOverlayLoading: (state, action: PayloadAction<{ id: string; isLoading: boolean }>) => {
      const { id, isLoading } = action.payload;
      if (isLoading) {
        if (!state.loadingOverlays.includes(id)) {
          state.loadingOverlays.push(id);
        }
      } else {
        state.loadingOverlays = state.loadingOverlays.filter(overlayId => overlayId !== id);
      }
    },
    setOverlayTruncated: (state, action: PayloadAction<boolean>) => {
      state.isOverlayTruncated = action.payload;
    }
  }
});

export const selectHasActivePopupOverlay = (state: { map: MapState }) => overlaysWithPopups.some(id => state.map.dynamicStyleIds.includes(id));

export const {
  resetState,

  updateCurrentCoordinates,
  updateElevationCoordinates,
  updateRouteCoordinates,

  updateCursor,
  updateIs3dActive,
  updateMapZoomBucket,

  updateClickedResult,
  updateHoveredAdminResult,
  updateHoveredResult,
  clearClickedCoordinates,
  updateRoutesById,
  updateAllowClickEvents,

  updateActiveTrails,
  addViewedPinSerializedCoordinatesThisSession,
  updateTrailResults,
  setHideTrailPins,
  updateNonTrailListResults,
  updateHoveredListItemId,

  updateActivePark,
  updateParkResults,

  updateActivePOI,
  updateHoveredPOI,
  updateSelectedPOI,
  updateIsMapFeatureSelected,
  setPendingFlyTo,
  clearPendingFlyTo,
  updateShowPOIPopupMenu,
  updatePoiResults,
  updateHiddenPOIs,
  updateRenderedPOIs,

  updatePhotospotResults,

  updateHoveredParkCoordinates,
  clearHoveredParkCoordinates,
  updateClickedParkCoordinates,
  clearClickedParkCoordinates,
  updateParks,

  setUserCustomRoutes,
  setDisplayedUserCustomRouteIds,
  removeUserCustomRoute,
  setActiveRouteIds,

  setUserRecordings,
  setDisplayedUserRecordingIds,
  removeUserRecording,

  setSelectedWaypoint,
  clearSelectedWaypoint,

  setUserWaypointDraft,
  clearUserWaypointDraft,

  setCommunityWaypointCategories,
  toggleCommunityWaypointCategory,

  updateAdminStyleSettings,
  addOrRemoveBoundaryFilter,
  updateBaseStyleId,
  toggleDynamicStyleId,
  toggleHeatmapStyleId,
  toggleStaticStyleId,
  updateDynamicStyleIds,
  updateHeatmapStyleIds,
  updateStaticStyleIds,
  updateSupportedDynamicStyleIds,

  setOverlayLoading,
  setOverlayTruncated
} = mapSlice.actions;

export const mapReducer = mapSlice.reducer;
